import dayjs from "dayjs";
import { CURRENCY_NEGATIVE_PATTERN, CURRENCY_POSITIVE_PATTERN, Formatting, Numeral } from "../../../../formatting";
import { DataTypes } from "../../../../metadata";
import { IColumn } from "../../../interfaces";
import { Record } from "../Record";
import numeral from "numeral";
import { IAddControlNotificationOptions, IColumnInfo, IDataProvider, IFieldChange, IFieldValidationResult, IRecord } from "../../interfaces";
import { FieldValidation } from "./FieldValidation";
import deepEqual from 'deep-eql';
import { sort } from "fast-sort";
import { Sanitizer } from "../../../../../helpers";

interface IColumnProperties {
    notificationsExpression: () => IAddControlNotificationOptions[];
    loadingExpression: () => boolean;
    heightExpression: (currentColumnWidth: number, rowHeight: number) => number | undefined;
    disabledExpression?: () => boolean;
    validationExpression?: () => IFieldValidationResult;
    requiredLevelExpression?: () => Xrm.Attributes.RequirementLevel;
    valueExpression?: () => any;
    cellEditorParametersExpression?: (parameters: any) => any
    currencySymbolExpression?: () => string;
}

//TODO: Field should be separated into multiple classes by data type where needed (to avoid the switch case on multiple places)
export class Field {
    private _record: Record;
    private _columnName: string;
    private _currentValue: any;
    private _originalRawValue: any;
    private _hasValueBeenUpdated: boolean = false;
    private _formatting: Readonly<Formatting>;
    private _dataProvider: IDataProvider;
    private _validation: FieldValidation;
    private _fieldProperties: IColumnProperties = {
        loadingExpression: () => false,
        notificationsExpression: () => [],
        heightExpression: () => undefined
    };

    constructor(record: Record, columnName: string, dataProvider: IDataProvider) {
        this._columnName = columnName;
        this._record = record;
        this._dataProvider = dataProvider;
        this._currentValue = this._getInitialValue();
        this._formatting = Formatting.Get();
        this._validation = new FieldValidation(this);
        this._dataProvider = dataProvider;
        this._originalRawValue = this._getRawValue(true);
    }

    public getColumn(): IColumn {
        const column = this._dataProvider.getColumns().find(x => x.name === this._columnName);
        if (!column) {
            throw new Error(`Column ${this._columnName} does not exists!`);
        }
        return column;
    }

    public setValue(value: any) {
        this._hasValueBeenUpdated = true;
        this._currentValue = value;
        this._dataProvider.fireEventListeners('onRecordColumnValueChanged', this._record, this._columnName);
    }

    public getValue(): any {
        let value;
        //user has manipulated with the value
        if (this._hasValueBeenUpdated) {
            value = this._currentValue;
        }
        //if undefined is returned, we ignore the expression
        else if (this._valueExpressionResult !== undefined) {
            value = this._valueExpressionResult;
        }
        //accesses the raw data
        else {
            value = this._getRawValue();
        }
        if (value === null || value === undefined) {
            return null;
        }
        return this._sanitizeValue(value);
    }

    public getFormattedValue(): string | null {
        const attributeMetadata = this.getColumn().metadata;
        let value = this.getValue();
        if (value === null || value === undefined) {
            return null;
        }
        switch (this._dataType) {
            case DataTypes.WholeNone: {
                if (typeof value === 'string') {
                    return value;
                }
                return this._formatting.formatInteger(value);
            }
            case DataTypes.WholeDuration: {
                if (typeof value === 'string') {
                    return value;
                }
                return this._formatting.formatDuration(value);
            }
            case DataTypes.Decimal: {
                const precision = attributeMetadata?.Precision;
                if (typeof value === 'string') {
                    return value;
                }
                return this._formatting.formatDecimal(value, precision);
            }
            case DataTypes.Currency: {
                const precision = attributeMetadata?.Precision;
                if (typeof value === 'string') {
                    return value;
                }
                return this._formatting.formatCurrency(value, precision, this._record.getCurrencySymbol(this._columnName));
            }
            case DataTypes.DateAndTimeDateOnly: {
                const date = dayjs(value);
                if (date.isValid()) {
                    return this._formatting.formatDateShort(date.toDate());
                }
                return value;
            }
            case DataTypes.DateAndTimeDateAndTime: {
                const behavior = attributeMetadata?.Behavior;
                const date = dayjs(value);
                if (date.isValid()) {
                    return this._formatting.formatTime(date.toDate(), behavior ?? 1);
                }
                return value;
            }
            case DataTypes.TwoOptions:
            case DataTypes.OptionSet: {
                const options = attributeMetadata?.OptionSet as ComponentFramework.PropertyHelper.OptionMetadata[] ?? [];
                return options.find(x => x.Value == value)?.Label ?? null;
            }
            case DataTypes.MultiSelectOptionSet: {
                const options = attributeMetadata?.OptionSet as ComponentFramework.PropertyHelper.OptionMetadata[] ?? [];
                return options.filter(option => value.includes(option.Value)).map(x => x.Label).join('; ');
            }
            case DataTypes.LookupCustomer:
            case DataTypes.LookupOwner:
            case DataTypes.LookupRegarding:
            case DataTypes.LookupSimple: {
                return value.map((x: ComponentFramework.EntityReference) => x.name)?.join('; ');
            }
            default: {
                return value;
            }
        }
    }
    public isDirty(): boolean {
        let initialValue = this._getInitialValue();
        let currentValue = this.getValue();
        switch (this._dataType) {
            //this is so we do not match multiselect dirty if we have the same values that has been selected in different order
            case DataTypes.MultiSelectOptionSet: {
                initialValue = initialValue ? sort(initialValue.split(',')).asc() : initialValue;
                currentValue = currentValue ? sort(currentValue.split(',')).asc() : initialValue;
            }
        }
        return !deepEqual(initialValue, currentValue);
    }

    public getChange(): IFieldChange | null {
        const that = this;
        if (!this.isDirty()) {
            this._hasValueBeenUpdated = false;
            return null;
        }
        return {
            columnName: this._columnName,
            get originalValue() {
                return that._getInitialValue();
            },
            get currentValue() {
                return that.getValue();
            },
            get isValid() {
                return !that.isValid().error;
            }
        };
    }
    public isValid() {
        return this._validation.isValid();
    }

    public getRequiredLevel(): Xrm.Attributes.RequirementLevel {
        let requiredLevel = this._fieldProperties.requiredLevelExpression?.();
        if (requiredLevel === undefined) {
            const metadataLevel = this._dataProvider.getColumns().find(x => x.name === this._columnName)?.metadata?.RequiredLevel ?? 0;
            switch (metadataLevel) {
                case 1:
                case 2: {
                    requiredLevel = 'required';
                    break;
                }
                default: {
                    requiredLevel = 'none';
                    break;
                }
            }
        }
        return requiredLevel!;
    }
    public isDisabled(): boolean {
        const isDisabled = this._fieldProperties.disabledExpression?.();
        if (isDisabled === undefined) {
            if (this.getColumn().metadata?.IsValidForUpdate) {
                return false;
            }
            return true;
        }
        else {
            return isDisabled;
        }
    }
    public isLoading(): boolean {
        return this._fieldProperties.loadingExpression();
    }
    public setFieldProperty(propertyName: keyof IColumnProperties, value: any) {
        if (typeof value === 'function') {
            this._fieldProperties[propertyName] = value;
        }
        else {
            this._fieldProperties[propertyName] = () => value;
        }
    }
    public getNotifications(): IAddControlNotificationOptions[] {
        return this._fieldProperties.notificationsExpression();
    }

    public getValidationExpression() {
        return this._fieldProperties.validationExpression;
    }

    public getCurrencySymbol(): string | null {
        return this._fieldProperties.currencySymbolExpression?.() ?? null;
    }

    public getRecord() {
        return this._record;
    }

    public getColumnInfo(): IColumnInfo {
        const that = this;
        return {
            //getters so the error checking runs when needed during runtime
            get error() {
                const validationResult = that.isValid();
                return validationResult.error;
            },
            get errorMessage() {
                const validationResult = that.isValid();
                return validationResult.errorMessage;
            },
            type: this._dataType,
            security: {
                get editable() {
                    return !that.isDisabled();
                },
                secured: false,
                readable: true,
                get requiredLevel() {
                    return that.getRequiredLevel();
                }
            }
        };
    }

    public getHeight(currentColumnWidth: number, rowHeight: number) {
        return this._fieldProperties.heightExpression(currentColumnWidth, rowHeight);
    }

    public getCellEditorParameters(currentParameters: any) {
        return this._fieldProperties.cellEditorParametersExpression?.(currentParameters);
    }

    private _getRawValue(doNotSanitize?: boolean) {
        let value;
        switch (this._dataType) {
            case DataTypes.LookupCustomer:
            case DataTypes.LookupOwner:
            case DataTypes.LookupRegarding:
            case DataTypes.LookupSimple: {
                value = this._getLookupReference();
                break;
            }
            default: {
                value = this._record.getRawData()?.[this._columnName];
                //provider does not have the data loaded => fallback to initial value
                if (value === undefined) {
                    //we have no data here, fallback to the last cached one
                    value = this._originalRawValue;
                }
            }
        }
        if (doNotSanitize) {
            return value;
        }
        return this._sanitizeValue(value);
    }

    private get _dataType() {
        return this.getColumn().dataType;
    }

    private _getLookupReference(): ComponentFramework.EntityReference | null {
        const rawData = this._record.getRawData();

        if (rawData[`_${this._columnName}_value`]) {
            return {
                id: {
                    guid: rawData[`_${this._columnName}_value`],
                },
                etn: rawData[`_${this._columnName}_value@Microsoft.Dynamics.CRM.lookuplogicalname`],
                name: rawData[`_${this._columnName}_value@OData.Community.Display.V1.FormattedValue`]
            };
        }
        else if (rawData[`${this._columnName}`]) {
            return {
                id: {
                    guid: rawData[`${this._columnName}`]
                },
                etn: rawData[`${this._columnName}@Microsoft.Dynamics.CRM.lookuplogicalname`],
                name: rawData[`${this._columnName}@OData.Community.Display.V1.FormattedValue`]
            };
        }
        return null;
    }

    private _createCurrencyPattern(pattern: string, numberPattern: string, currencySymbol: string) {
        const escapedPattern = this._escapeRegExp(pattern);
        const escapedCurrencySymbolPattern = `(${this._escapeRegExp(currencySymbol)})?`;
        const finalPattern = escapedPattern.replace('\\$', escapedCurrencySymbolPattern).replace('n', numberPattern);
        return new RegExp(`^${finalPattern.replace(/\s/g, '')}$`);
    }

    private _escapeRegExp(string: string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    private _sanitizeValue(value: any) {
        if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) {
            return null;
        }
        switch (this._dataType) {
            //always returns string, but can be set by 1/0 string/number or by boolean
            case DataTypes.TwoOptions: {
                if (typeof value === 'boolean') {
                    value = value ? '1' : '0';
                }
                if (typeof value === 'number') {
                    value = `${value}`;
                }
                break;
            }
            //always returns a string
            case DataTypes.OptionSet: {
                //can be set by number or string
                value = `${value}`;
                break;
            }
            //always returns an string
            case DataTypes.MultiSelectOptionSet: {
                //passed as number
                if (typeof value === 'number') {
                    value = `${value}`;
                }
                //passed as array, eg [7000001, 70000002], both string and number supported
                if (Array.isArray(value)) {
                    value = value.map(x => x).join(',');
                }
                //if passed as string, the values should be comma separated, eg '7000001,70000002'
                break;
            }
            //always returns a number
            case DataTypes.Currency: {
                if (typeof value === 'number') {
                    break;
                }
                const currencySymbol = this._record.getCurrencySymbol(this._columnName);
                const numberFormatting = this._formatting.numberFormattingInfo;
                const numberPattern = `\\d{1,}(${numberFormatting.currencyGroupSeparator}\\d{1,})*(\\${numberFormatting.currencyDecimalSeparator}\\d+)?`;
                const positivePattern = this._createCurrencyPattern(CURRENCY_POSITIVE_PATTERN[numberFormatting.currencyPositivePattern], numberPattern, currencySymbol);
                const negativePattern = this._createCurrencyPattern(CURRENCY_NEGATIVE_PATTERN[numberFormatting.currencyNegativePattern], numberPattern, currencySymbol);
                Numeral.currency({ ...numberFormatting, currencySymbol: currencySymbol });
                if (positivePattern.test(value)) {
                    value = numeral(value).value() ?? undefined;
                }
                else if (negativePattern.test(value)) {
                    const candidate = numeral(value).value()!;
                    if (candidate > 0) {
                        value = candidate * -1;
                    }
                    else {
                        value = candidate;
                    }
                }
                break;
            }
            //always returns ComponentFramework.EntityReference, DIFFERENCE FROM POWER APPS: we always return an array, because lookups can support them
            case DataTypes.LookupCustomer:
            case DataTypes.LookupOwner:
            case DataTypes.LookupRegarding:
            case DataTypes.LookupSimple: {
                if (!Array.isArray(value)) {
                    value = [value];
                }
                value = value.map((x: any) => Sanitizer.Lookup.getEntityReference(x));
                break;
            }
            case DataTypes.DateAndTimeDateOnly:
            case DataTypes.DateAndTimeDateAndTime: {
                if (value instanceof Date) {
                    const attributeMetadata = this.getColumn().metadata;
                    const date = dayjs(value);
                    if (attributeMetadata?.DateTimeBehavior === 'DateOnly') {
                        value = date.format('YYYY-MM-DD');
                    }
                    else {
                        value = date.toISOString();
                    }
                }
                break;
            }
        }
        return value;
    }

    private _getInitialValue() {
        if (this._valueExpressionResult !== undefined) {
            return this._sanitizeValue(this._valueExpressionResult);
        }
        else {
            return this._getRawValue();
        }
    }

    private get _valueExpressionResult(): any {
        return this._fieldProperties.valueExpression?.();
    }
}